<script setup>
import {ref, computed, watch, onMounted, onBeforeUnmount} from 'vue'
import {useStore} from 'vuex'
import {useRoute} from 'vue-router'
import {Plus, Undo2, Satellite, Circle, Hexagon, MessageCircle} from 'lucide-vue-next'
import { area, centroid, length } from '@turf/turf'
import { isClosedLineString, closedLineStringToPolygon, hasAreaGeometry, formatArea, MAX_MONITORING_AREA_SQKM } from '@/utils/geometry'
import {Button} from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {FormFeature} from '@/components/forms'
import {Point, LineString, Polygon} from '@/components/icons'
import {MapFileInput} from '@/components/map'
import { ProjectSelect } from '@/components/ui/project-select'
import { setDrawColor, resetDrawColor } from '@/utils/mapbox/drawColor'

const props = defineProps({
  isLayersView: {
    type: Boolean,
    default: false,
  },
  // Split-screen: when provided, draw binds to this tile's own map/draw instead
  // of the global canvas, and a newly-drawn layer is routed to targetMapId.
  map: {
    type: Object,
    default: null,
  },
  draw: {
    type: Object,
    default: null,
  },
  targetMapId: {
    type: String,
    default: null,
  },
})

const route = useRoute()
const projectId = computed(() => parseInt(route.params.project_id))

const store = useStore()
const map = computed(() => props.map || store.getters['geo/getMap'])
const draw = computed(() => props.draw || store.getters['geo/getDraw'])
const allTemplates = computed(() => store.getters['geo/getAllTemplates'])
const projects = computed(() => store.getters['project/getFlatedProjects'])
const selectedProjectFilter = ref('all')
const selectedOrg = computed(() => store.getters['org/getSelectedOrg'])

const activeAction = ref(null)

const isOpen = ref(false)
const selectedGeometry = ref(null)
const drawedData = ref([])

// Live area HUD while drawing a polygon/circle (ported from the bia prototype).
const liveDrawMode = ref(null)
const liveAreaSqm = ref(null)

const isMonitoringDraw = computed(() => !!activeAction.value?.monitor_area)
const liveAreaText = computed(() => liveAreaSqm.value ? formatArea(liveAreaSqm.value) : null)
const liveOverLimit = computed(() =>
  isMonitoringDraw.value && liveAreaSqm.value / 1e6 > MAX_MONITORING_AREA_SQKM)
const liveDrawHint = computed(() => liveDrawMode.value === 'draw_circle'
  ? 'click to finish · Esc to cancel'
  : 'double-click or Enter to finish · Esc to cancel')

const stopLiveArea = () => {
  liveDrawMode.value = null
  liveAreaSqm.value = null
}

const onModeChange = (e) => {
  if (e.mode === 'draw_polygon' || e.mode === 'draw_circle') {
    liveDrawMode.value = e.mode
  } else {
    stopLiveArea()
  }
}

// The in-progress shape is the last feature in draw's collection and includes
// the trailing cursor vertex, so the readout tracks the mouse in real time.
const onLiveMouseMove = () => {
  if (!liveDrawMode.value) return
  const features = draw.value.getAll().features
  const current = features[features.length - 1]
  if (current?.geometry.type === 'Polygon' && current.geometry.coordinates[0]?.length >= 4) {
    liveAreaSqm.value = area(current)
  }
}

// Outside the layers view the create panel is strictly scoped to the routed
// project; the picker only exists in the layers view, where drawing produces
// org layers rather than project data.
const targetProjectId = computed(() => props.isLayersView ? selectedProjectFilter.value : (projectId.value || null))

const filteredTemplates = computed(() => {
  if (!allTemplates.value) return []

  if (targetProjectId.value === 'all') {
    return allTemplates.value
  }

  if (!targetProjectId.value) return []

  return allTemplates.value.filter(t => t.project_id === parseInt(targetProjectId.value))
})

const templatesByProject = computed(() => {
  if (!filteredTemplates.value) return {}

  const grouped = {}
  filteredTemplates.value.forEach(template => {
    const projectName = template.project_name || 'Unknown Project'
    if (!grouped[projectName]) {
      grouped[projectName] = []
    }
    grouped[projectName].push(template)
  })

  return grouped
})

const templateAction = (template) => ({
  name: template.name,
  type: template.geometry_type === 'Point' ? 'draw_point' : template.geometry_type === 'LineString' ? 'draw_line_string' : 'draw_polygon',
  color: template.color,
  s3_icon: template.s3_icon,
  template_id: template.id,
  project_id: template.project_id,
  project_name: template.project_name
})

const rowClass = 'w-full justify-start gap-2.5 h-auto px-2 py-1.5 font-normal text-sm'
const iconChipClass = 'w-6 h-6 rounded-full flex items-center justify-center shrink-0'
const sectionHeaderClass = 'px-2 pb-1 text-xs font-semibold text-muted-foreground'

const templateSections = computed(() => {
  if (!filteredTemplates.value.length) return []

  if (targetProjectId.value === 'all') {
    return Object.entries(templatesByProject.value).map(([title, templates]) => ({title, templates}))
  }

  return [{title: 'Templates', templates: filteredTemplates.value}]
})

const defaultGeometryActions = [
  {name: 'Point', type: 'draw_point', color: '0059ff', icon: 'point'},
  {name: 'Line', type: 'draw_line_string', color: '0059ff', icon: 'line-string'},
  {name: 'Polygon', type: 'draw_polygon', color: '0059ff', icon: 'polygon'},
]

const monitoringPopoverOpen = ref(false)
const monitoringDisabled = computed(() => {
  const id = parseInt(targetProjectId.value)
  return !!id && !store.getters['project/isMonitoringEnabled'](id)
})
const monitoringShapes = [
  {label: 'Circle', type: 'draw_circle', icon: Circle},
  {label: 'Polygon', type: 'draw_polygon', icon: Hexagon},
]

const chatEnabled = computed(
  () => !!targetProjectId.value && targetProjectId.value !== 'all' &&
    store.getters['discussion/isEnabled'](targetProjectId.value)
)

const messagePopoverOpen = ref(false)
const messageShapes = [
  {label: 'Point', type: 'draw_point', icon: Point, iconClass: 'fill-current'},
  {label: 'Line', type: 'draw_line_string', icon: LineString, iconClass: 'fill-current'},
  {label: 'Polygon', type: 'draw_polygon', icon: Polygon, iconClass: 'fill-current'},
  {label: 'Circle', type: 'draw_circle', icon: Circle, iconClass: ''},
]

const startMessageDraw = (shape) => {
  messagePopoverOpen.value = false
  startDraw({name: 'Message', type: shape.type, color: '0059ff', message: true})
}

const startMonitoringDraw = (shape) => {
  monitoringPopoverOpen.value = false
  startDraw({name: 'Monitoring Area', type: shape.type, color: '0059ff', monitor_area: true})
}

// No need for combined actions since we handle them separately in the template

const toggle = () => {
  isOpen.value = !isOpen.value


  if (isOpen.value && selectedOrg.value) {
    store.dispatch('geo/fetchAllTemplates', selectedOrg.value.organization_id)
  }
}

const onDrawUpdate = async (e) => {
  if (e?.type === 'draw.create') {
    stopLiveArea()
  }
  // A drawn message shape never becomes a feature: it opens the on-map compose
  // bubble at its center and lives on the thread itself.
  if (e?.type === 'draw.create' && activeAction.value?.message && e.features?.length) {
    const feature = e.features[0]
    const center = centroid(feature).geometry.coordinates
    store.commit('discussion/SET_ACTIVE_THREAD', null)
    store.commit(
      'discussion/SET_MAP_DRAFT',
      feature.geometry.type === 'Point' ? center : {location: center, geometry: feature.geometry}
    )
    close()
    return
  }
  if (e?.type === 'draw.create' && e.features?.length) {
    const newIds = new Set(e.features.map((f) => f.id))
    for (const f of draw.value.getAll().features) {
      if (newIds.has(f.id)) continue
      cleanUpFeature(f)
      try {
        draw.value.delete(f.id)
      } catch (err) {
        console.warn('[DialogCreate] failed to delete leftover feature', f.id, err)
      }
    }
  }

  const data = draw.value.getAll()
  drawedData.value = []

  for (const [index, feature] of data.features.entries()) {
    let areaText = ''

    if (hasAreaGeometry(feature.geometry)) {
      const areaInSm = area(feature)
      const areaInHa = (areaInSm / 10000).toFixed(3) // 1 ha = 10000 sm
      const areaInSkm = (areaInSm / 1000000).toFixed(4) // 1 skm = 1000000 sm
      areaText = `${areaInHa} ha (${areaInSkm} km²)`

      feature.properties = {
        ...feature.properties,
        areaInSm: areaInSm,
        areaInHa: areaInHa,
        areaInSkm: areaInSkm,
        areaText: areaText
      }
    }

    if (feature.geometry.type === 'LineString') {
      // Check if it's a closed LineString (circle/buffer)
      if (isClosedLineString(feature.geometry)) {
        const polygonGeom = closedLineStringToPolygon(feature.geometry)
        const areaInSm = area({ type: 'Feature', geometry: polygonGeom })
        const areaInHa = (areaInSm / 10000).toFixed(3)
        const areaInSkm = (areaInSm / 1000000).toFixed(4)
        areaText = `${areaInHa} ha (${areaInSkm} km²)`

        feature.properties = {
          ...feature.properties,
          areaInSm: areaInSm,
          areaInHa: areaInHa,
          areaInSkm: areaInSkm,
          areaText: areaText
        }
      } else {
        const lengthInM = length(feature, { units: 'meters' })
        const lengthInKm = (lengthInM / 1000).toFixed(2)
        areaText = `${lengthInKm} km`

        feature.properties = {
          ...feature.properties,
          lengthInM: lengthInM,
          lengthInKm: lengthInKm,
          areaText: areaText
        }
      }
    }

    const areaCenter = centroid(feature)
    const sourceId = 'areaLabelSource-' + feature.id
    const layerId = 'areaLabel-' + feature.id

    // Add color from activeAction if using a template
    // Ensure color always has # prefix
    let color = '#0059ff' // default color
    if (activeAction.value?.color) {
      // Handle both formats (with and without #)
      color = activeAction.value.color.startsWith('#') ? 
        activeAction.value.color : 
        `#${activeAction.value.color}`
    }
    
    feature.properties = {
      ...feature.properties,
      areaCenter: areaCenter,
      color: color,
      user_color: color
    }

    try {
      draw.value.setFeatureProperty(feature.id, 'user_color', color)
    } catch (e) {
      console.warn('[DialogCreate] setFeatureProperty failed for', feature.id, e)
    }

    drawedData.value.push(feature)

    if (map.value.getSource(sourceId)) {
      map.value.getSource(sourceId).setData(areaCenter)
      map.value.setLayoutProperty(layerId, 'text-field', areaText)
    } else {
      map.value.addSource(sourceId, {
        type: 'geojson',
        data: areaCenter
      })

      map.value.addLayer({
        id: layerId,
        type: 'symbol',
        source: sourceId,
        layout: {
          'text-field': areaText,
          'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
          'text-radial-offset': 0.5,
          'text-justify': 'auto',
          'text-size': 14,
          'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold']
        },
        paint: {
          'text-color': '#1a1a1a',
          'text-halo-color': '#ffffff',
          'text-halo-width': 2
        }
      })
    }
  }
}

const reset = () => {
  // draw.deleteAll() is silent (no draw.delete event), so manually clean up
  // each feature's area-label source/layer here before wiping the store —
  // otherwise stale area labels accumulate on the map across resets.
  if (draw.value) {
    for (const feature of draw.value.getAll().features) {
      cleanUpFeature(feature)
    }
  }

  activeAction.value = null
  draw.value.deleteAll()
  drawedData.value = []
  selectedGeometry.value = null
  stopLiveArea()
  resetDrawColor(map.value)
}

const startDraw = (action) => {
  reset()
  activeAction.value = action
  const color = action.color?.startsWith('#') ? action.color : `#${action.color}`
  setDrawColor(map.value, color)
  draw.value.changeMode(action.type)
  // changeMode() doesn't emit draw.modechange, so seed the HUD state here.
  if (!action.message && (action.type === 'draw_polygon' || action.type === 'draw_circle')) {
    liveDrawMode.value = action.type
  }
}

const handleFeatureUpdate = (updatedFeature) => {
  // Update the selected geometry with new properties (like color)
  if (selectedGeometry.value && updatedFeature) {
    selectedGeometry.value = updatedFeature

    // Find the draw feature that needs updating
    const drawFeatures = draw.value.getAll()
    const drawFeature = drawFeatures.features.find(f =>
      f.properties?.local_uuid === updatedFeature.properties?.local_uuid ||
      f.id === selectedGeometry.value.id
    )

    if (drawFeature) {
      // Store the feature ID for re-adding
      const featureId = drawFeature.id

      // Update properties including color
      drawFeature.properties = {
        ...drawFeature.properties,
        ...updatedFeature.properties
      }

      // Delete and re-add the feature to force style update
      draw.value.delete(featureId)

      // Re-add with the same ID to maintain consistency
      const updatedDrawFeature = {
        ...drawFeature,
        id: featureId,
        properties: {
          ...drawFeature.properties,
          color: updatedFeature.properties?.color || '#0059ff',
          user_color: updatedFeature.properties?.color || '#0059ff'
        }
      }

      draw.value.add(updatedDrawFeature)

      // Update drawedData to maintain consistency
      const dataIndex = drawedData.value.findIndex(item => item.id === featureId)
      if (dataIndex !== -1) {
        drawedData.value[dataIndex] = {
          ...drawedData.value[dataIndex],
          properties: updatedDrawFeature.properties
        }
      }
    }
  }
}

const close = () => {
  isOpen.value = false
  reset()
}

const cleanUpFeature = async (feature) => {
  const sourceId = 'areaLabelSource-' + feature.id
  const layerId = 'areaLabel-' + feature.id
  if (map.value.getLayer(layerId)) {
    map.value.removeLayer(layerId)
  }
  if (map.value.getSource(sourceId)) {
    map.value.removeSource(sourceId)
  }
}

const onDrawDelete = async (e) => {
  for (const feature of e.features) {
    cleanUpFeature(feature)
  }
}

onMounted(() => {
  // Fetch templates on mount in case dialog is already open
  if (isOpen.value && selectedOrg.value) {
    store.dispatch('geo/fetchAllTemplates', selectedOrg.value.organization_id)
  }

  map.value.on('draw.create', onDrawUpdate)
  map.value.on('draw.update', onDrawUpdate)
  map.value.on('draw.delete', onDrawDelete)
  map.value.on('draw.modechange', onModeChange)
  map.value.on('mousemove', onLiveMouseMove)
})

onBeforeUnmount(() => {
  if (!map.value) return
  map.value.off('draw.create', onDrawUpdate)
  map.value.off('draw.update', onDrawUpdate)
  map.value.off('draw.delete', onDrawDelete)
  map.value.off('draw.modechange', onModeChange)
  map.value.off('mousemove', onLiveMouseMove)
})

// No need to watch selectedProjectFilter - filtering happens in computed property

watch(() => drawedData.value, (value) => {
  if (value.length === 0) {
    selectedGeometry.value = null
  }

  if (value.length > 0 && selectedGeometry.value === null) {
    selectedGeometry.value = value[0]
  }
})
</script>

<template>
  <div>
    <div
      v-if="liveDrawMode"
      class="fixed bottom-8 left-1/2 -translate-x-1/2 z-30 pointer-events-none rounded-full bg-background/95 backdrop-blur border shadow-lg px-4 py-2 text-sm whitespace-nowrap"
      :class="{'border-destructive': liveOverLimit}"
      data-testid="live-area-hud"
    >
      <template v-if="liveAreaText">
        <span class="font-semibold" :class="{'text-destructive': liveOverLimit}">{{ liveAreaText }}</span>
        <span v-if="liveOverLimit" class="text-destructive font-medium"> — over the {{ MAX_MONITORING_AREA_SQKM }} km² monitoring limit</span>
        <span v-else-if="isMonitoringDraw" class="text-muted-foreground"> · monitoring limit {{ MAX_MONITORING_AREA_SQKM }} km²</span>
        <span class="text-muted-foreground"> · {{ liveDrawHint }}</span>
      </template>
      <template v-else>
        <span class="font-medium">Click the map to start your area</span>
        <span class="text-muted-foreground"> · Esc to cancel</span>
      </template>
    </div>
    <Transition name="slideToolbox">
      <div v-if="isOpen" class="bg-background rounded-lg shadow-lg w-[280px] max-h-[70vh] overflow-y-auto mb-2 -mr-11 lg:mr-0 p-2">
        <div v-if="props.isLayersView && allTemplates && allTemplates.length > 0" class="mb-3">
          <ProjectSelect
            :projects="projects"
            :model-value="selectedProjectFilter"
            :show-all-option="true"
            placeholder="Filter templates by project"
            @update:model-value="(val) => selectedProjectFilter = val"
          />
        </div>
        <div v-if="!targetProjectId" class="px-2 py-3 text-sm text-muted-foreground text-center">
          Select a project to add data.
        </div>
        <div v-else>
          <div :class="sectionHeaderClass">Data</div>

          <MapFileInput @change="() => close()" />

          <Button
            v-for="action of defaultGeometryActions"
            :key="action.name"
            variant="ghost"
            type="button"
            :class="[rowClass, {'bg-accent font-medium': activeAction && action.name === activeAction.name}]"
            :aria-label="`Draw ${action.name} on map`"
            :aria-pressed="activeAction && action.name === activeAction.name"
            :title="`Draw ${action.name} on map`"
            :data-testid="`draw-${action.type}-button`"
            :id="`draw-${action.name.toLowerCase()}-button`"
            @click="startDraw(action)"
          >
            <span :class="iconChipClass" :style="{backgroundColor: '#' + action.color}">
              <Point class="fill-white w-3.5 h-3.5" v-if="action.icon === 'point'" aria-hidden="true" />
              <LineString class="fill-white w-3.5 h-3.5" v-else-if="action.icon === 'line-string'" aria-hidden="true" />
              <Polygon class="fill-white w-3.5 h-3.5" v-else-if="action.icon === 'polygon'" aria-hidden="true" />
            </span>
            <span class="truncate">{{ action.name }}</span>
          </Button>

          <TooltipProvider :delayDuration="450">
            <Tooltip>
              <TooltipTrigger as-child>
                <span class="block">
                  <Popover v-model:open="monitoringPopoverOpen">
                    <PopoverTrigger as-child>
                      <Button
                        variant="ghost"
                        type="button"
                        :class="[rowClass, {'bg-accent font-medium': activeAction && activeAction.name === 'Monitoring Area'}]"
                        :disabled="monitoringDisabled"
                        aria-label="Draw Monitoring Area on map"
                        :aria-pressed="activeAction && activeAction.name === 'Monitoring Area'"
                        :title="monitoringDisabled ? 'Monitoring is turned off for this project' : 'Draw Monitoring Area on map'"
                        data-testid="draw-monitoring-area-button"
                        id="draw-monitoring-area-button"
                      >
                        <span :class="iconChipClass" style="background-color: #0059ff;">
                          <Satellite class="w-3.5 h-3.5 text-white" aria-hidden="true" />
                        </span>
                        <span class="truncate">Monitoring Area</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent class="w-auto p-1" align="start" side="right">
                      <Button
                        v-for="shape in monitoringShapes"
                        :key="shape.type"
                        variant="ghost"
                        type="button"
                        :class="rowClass"
                        :data-testid="`draw-monitoring-${shape.label.toLowerCase()}-button`"
                        @click="startMonitoringDraw(shape)"
                      >
                        <component :is="shape.icon" class="w-4 h-4 shrink-0" />
                        <span>{{ shape.label }}</span>
                      </Button>
                    </PopoverContent>
                  </Popover>
                </span>
              </TooltipTrigger>
              <TooltipContent v-if="monitoringDisabled">
                <p>Monitoring is turned off for this project — enable it in project settings.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <Popover v-if="chatEnabled" v-model:open="messagePopoverOpen">
            <PopoverTrigger as-child>
              <Button
                variant="ghost"
                type="button"
                :class="[rowClass, {'bg-accent font-medium': activeAction && activeAction.name === 'Message'}]"
                aria-label="Add a message on the map"
                :aria-pressed="activeAction && activeAction.name === 'Message'"
                title="Add a message on the map"
                data-testid="add-map-message-button"
                id="add-map-message-button"
              >
                <span :class="iconChipClass" style="background-color: #0059ff;">
                  <MessageCircle class="w-3.5 h-3.5 text-white" aria-hidden="true" />
                </span>
                <span class="truncate">Message</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent class="w-auto p-1" align="start" side="right">
              <Button
                v-for="shape in messageShapes"
                :key="shape.type"
                variant="ghost"
                type="button"
                :class="rowClass"
                :data-testid="`draw-message-${shape.label.toLowerCase()}-button`"
                @click="startMessageDraw(shape)"
              >
                <component :is="shape.icon" class="w-4 h-4 shrink-0" :class="shape.iconClass" />
                <span>{{ shape.label }}</span>
              </Button>
            </PopoverContent>
          </Popover>

          <template v-for="section of templateSections" :key="section.title">
            <div :class="[sectionHeaderClass, 'mt-1 pt-2 border-t border-border']">{{ section.title }}</div>
            <Button
              v-for="template of section.templates"
              :key="template.id"
              variant="ghost"
              type="button"
              :class="[rowClass, {'bg-accent font-medium': activeAction && template.id === activeAction?.template_id}]"
              :aria-label="`Draw ${template.name} on map`"
              :aria-pressed="activeAction && template.id === activeAction?.template_id"
              :title="`Draw ${template.name} on map`"
              :data-testid="`draw-template-${template.id}-button`"
              :id="`draw-template-${template.id}-button`"
              @click="startDraw(templateAction(template))"
            >
              <span :class="iconChipClass" :style="{backgroundColor: template.color}">
                <img v-if="template.s3_icon" class="object-contain w-3.5 h-3.5" :src="template.s3_icon" :alt="`${template.name} icon`">
                <Point v-else-if="template.geometry_type === 'Point'" class="fill-white w-3.5 h-3.5" aria-hidden="true" />
                <LineString v-else-if="template.geometry_type === 'LineString'" class="fill-white w-3.5 h-3.5" aria-hidden="true" />
                <Polygon v-else-if="template.geometry_type === 'Polygon'" class="fill-white w-3.5 h-3.5" aria-hidden="true" />
              </span>
              <span class="truncate">{{ template.name }}</span>
            </Button>
          </template>

          <div v-if="activeAction" class="mt-1 pt-1 border-t border-border">
            <Button
              variant="ghost"
              type="button"
              :class="rowClass"
              aria-label="Reset drawing tools and clear selection"
              title="Reset drawing tools and clear selection"
              data-testid="reset-draw-button"
              id="reset-draw-button"
              @click="reset()"
            >
              <span :class="[iconChipClass, 'bg-muted']">
                <Undo2 class="w-3.5 h-3.5 text-foreground" aria-hidden="true" />
              </span>
              <span class="truncate">Reset</span>
            </Button>
          </div>
        </div>

        <div v-if="activeAction && drawedData.length > 0" class="mt-3 pt-3 border-t border-border">
          <div>
            <FormFeature
              v-if="selectedGeometry"
              :feature="selectedGeometry"
              :template-id="activeAction?.template_id ? activeAction.template_id.toString() : null"
              :template-project-id="activeAction?.project_id ? activeAction.project_id.toString() : null"
              :predefined-monitor-area="activeAction?.monitor_area ?? false"
              :is-layers-view="props.isLayersView"
              :target-map-id="props.targetMapId"
              @update:created="close()"
            />
          </div>
        </div>
      </div>
    </Transition>
    <div class="flex items-center justify-end">
      <Button
        @click="toggle"
        class="rounded-full shadow-md h-14 w-14 p-0 bg-primary hover:bg-primary/90 text-white"
        aria-label="Create new data feature"
        title="Create new data feature"
        data-testid="create-data-button"
        id="create-data-button"
        :aria-expanded="isOpen"
        aria-haspopup="true"
        type="button"
        role="button"
      >
        <Plus
          :class="{'rotate-45': isOpen}"
          class="w-8 h-8 transition duration-150 ease-out"
          aria-hidden="true"
        />
        <span class="sr-only">
          {{ isOpen ? 'Close' : 'Open' }} drawing tools
        </span>
      </Button>
    </div>
  </div>
</template>

<style>
.slideToolbox-enter-active, .slideToolbox-leave-active {
  transition: transform 0.1s ease-in-out, opacity 0.1s ease-in-out;
}
.slideToolbox-enter-from, .slideToolbox-leave-to {
  transform: translateY(10%);
  opacity: 0;
}
</style>
